import { DatabaseEntryChartResultPipeline } from '@/composables/charts/DatabaseEntryChartResultPipeline'
import DimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/DimensionResolver'
import FallbackDimensionResolver from '@/composables/charts/influx-charts/dimension-resolvers/FallbackDimensionResolver'
import _ from 'lodash'

/**
 * Concrete implementation of DatabaseEntryChartResultPipeline for generic entries.
 * Use this for any database entries that have a simple timestamp property.
 *
 * Example usage:
 * const pipeline = new GenericEntryChartResultPipeline()
 * const charts = await pipeline.prepareData(
 *   events,
 *   'timestamp', // or 'createdAt', 'occurredAt', etc.
 *   ['category', 'severity'],
 *   timeSetup
 * )
 */
export class GenericEntryChartResultPipeline<T = any> extends DatabaseEntryChartResultPipeline<T> {
  private customResolverClasses: (typeof DimensionResolver)[] = []

  /**
   * Create a new GenericEntryChartResultPipeline
   * @param resolverClasses Optional custom resolver classes. Defaults to [FallbackDimensionResolver]
   */
  constructor(resolverClasses?: (typeof DimensionResolver)[]) {
    super()
    this.customResolverClasses = resolverClasses || [FallbackDimensionResolver]
  }

  /**
   * Get the supported resolver classes for this pipeline
   * Defaults to FallbackDimensionResolver but can be customized via constructor
   */
  protected getSupportedResolverClasses(): (typeof DimensionResolver)[] {
    return this.customResolverClasses
  }

  /**
   * Extract timestamp from an entry using lodash get for nested properties
   * Supports dot notation like 'metadata.timestamp' or 'details.createdAt'
   */
  protected getTimestampFromEntry(entry: T, timestampProperty: string): number | null {
    const timestamp = _.get(entry, timestampProperty)

    if (timestamp === null || timestamp === undefined) {
      return null
    }

    // Handle both number timestamps and Date objects
    if (typeof timestamp === 'number') {
      return timestamp
    }

    if (timestamp instanceof Date) {
      return timestamp.getTime()
    }

    // Try to parse string dates
    if (typeof timestamp === 'string') {
      const parsed = Date.parse(timestamp)
      return isNaN(parsed) ? null : parsed
    }

    return null
  }

  setResolverClasses(resolverClasses: (typeof DimensionResolver)[]): void {
    this.customResolverClasses = resolverClasses
  }

  addResolverClass(resolverClass: typeof DimensionResolver): void {
    if (!this.customResolverClasses.includes(resolverClass)) {
      this.customResolverClasses.push(resolverClass)
    }
  }
}
